import { fetchQuery, graphql } from 'react-relay';

import { APIConfig } from 'src/config';
import {
  DEFAULT_FETCH_QUERY_TIMEOUT, DEFAULT_MAX_RETRIES, METER_LIST_PAGE_SIZE,
} from 'src/util/constants';
import { getNodesFromEdges, queryWithRetry } from 'src/util/graphql';
import logger from 'src/util/logger';

// Query: viewer meters. Paginated to handle users with many meters so that the
// "Accept" dropdown is built from all active meters.
const viewerMetersQuery = graphql`
query meterFetchingViewerMetersQuery(
  $pageSize: Int,
  $after: String
) {
  viewer {
    meters(first: $pageSize, after: $after) {
      edges {
        node {
          id
          identifier
          title
          tradePointId
          active { start finish }
          property {
            id
            title
            active { start finish }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

/**
 * Exhaustively fetches all meters for the current viewer with pagination support.
 * Mirrors the getMeters pattern used for property data batching.
 * @param {object} args - Arguments for fetching meters
 * @param {object} args.environment - Relay environment
 * @param {number} [args.pageSize=METER_LIST_PAGE_SIZE] - Number of meters to fetch per page
 * @param {AbortSignal} [args.signal] - Optional signal used to abort pagination early. When the
 *   signal is aborted (e.g. on component unmount/navigation), no further pages are fetched.
 * @returns {Promise<{ meters: Array, complete: boolean }>} Promise that resolves to the flat array
 *   of meter nodes and a `complete` flag. `complete` is `false` when pagination stopped early on a
 *   degraded path (missing data, broken cursor invariant, or a per-page fetch error), meaning the
 *   returned list may be a partial subset of the viewer's meters.
 */
export const getViewerMeters = async (args) => {
  const {
    environment,
    pageSize = METER_LIST_PAGE_SIZE,
    signal,
  } = args;

  const meters = [];
  let hasNextPage = true;
  let afterCursor = '';
  let complete = true;

  while (hasNextPage) {
    if (signal?.aborted) {
      logger.log('getViewerMeters aborted; stopping pagination');
      break;
    }

    const variables = {
      pageSize,
      ...(afterCursor && { after: afterCursor }),
    };

    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await queryWithRetry({
        queryFn: () => {
          let resolvePromise;
          let rejectPromise;

          const promise = new Promise((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
          });

          const subscription = fetchQuery(environment, viewerMetersQuery, variables).subscribe({
            next: (result) => {
              resolvePromise(result);
            },
            error: (error) => {
              rejectPromise(error);
            },
          });

          promise.unsubscribe = () => subscription.unsubscribe();

          return promise;
        },
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: 'Fetching viewer meters',
        maxRetries: DEFAULT_MAX_RETRIES,
      });

      if (!data?.viewer?.meters?.edges) {
        logger.warn('No viewer meters data returned');
        complete = false;
        break;
      }

      const pageMeters = getNodesFromEdges(data.viewer.meters);
      meters.push(...pageMeters);

      hasNextPage = data.viewer.meters.pageInfo?.hasNextPage || false;
      afterCursor = data.viewer.meters.pageInfo?.endCursor || '';

      if (hasNextPage && !afterCursor) {
        logger.error('hasNextPage is true but endCursor is missing; stopping pagination to avoid infinite loop');
        complete = false;
        break;
      }
    } catch (error) {
      logger.error('Error fetching viewer meters page:', error);
      complete = false;
      break; // Exit pagination loop on error
    }
  }

  logger.log(`getViewerMeters retrieved ${meters.length} meters`);
  return { meters, complete };
};

export default getViewerMeters;
